import React, { useRef, useState } from 'react';
import { FiLock, FiX } from 'react-icons/fi';
import { cancelRun, getCredentialsStatus, getHandoffLink, getStatus, runJob, shareCarrierSession, type CredentialStatus } from '../../services/automationService';
import { CARRIER_CATALOG } from './carrierAccessCatalog';

/**
 * "Log in to all carriers": logs the carrier browser in to every carrier
 * with a stored login, one at a time, using the same login engine and the
 * same hand-off cards as the chat (automation/lib/carrierLogin.ts). Progress
 * is reported per carrier so each carrier tile can show its own status and,
 * when it is waiting on the person, its card.
 */

export type LoginProgress = {
  state: 'waiting' | 'working' | 'needs-you' | 'in' | 'out' | 'skipped';
  note?: string;
  /** Signed hand-off link while the carrier is waiting on a verification choice or a code. */
  card?: string | null;
  /** The login's run, so the person can cancel it (the X on the tile and card). */
  runId?: string;
};

const LogInAllCarriers: React.FC<{ onProgress: (progress: Record<string, LoginProgress>) => void; onFinished?: () => void }> = ({ onProgress, onFinished }) => {
  const [running, setRunning] = useState(false);
  const stopRef = useRef(false);
  const currentRun = useRef<string | null>(null);
  const progress = useRef<Record<string, LoginProgress>>({});
  const set = (id: string, p: LoginProgress) => { progress.current = { ...progress.current, [id]: p }; onProgress(progress.current); };

  const start = async () => {
    stopRef.current = false;
    setRunning(true);
    let status: Record<string, CredentialStatus> = {};
    try { status = await getCredentialsStatus(); } catch { setRunning(false); return; }
    // Carriers with a stored login are logged in by the login engine. Carriers that only have a carrier-browser session (Travelers:
    // its sign-in will not take a typed password) are checked instead: a session that is still good needs no code or password.
    const startUrl = (id: string) => CARRIER_CATALOG.find((c) => c.id === id)?.browserSession?.startUrl;
    const sessionOnly = (id: string) => status[id] !== undefined && status[id]?.loginStored === undefined && !!startUrl(id);
    const ids = CARRIER_CATALOG.filter((c) => !c.locked).map((c) => c.id).filter((id) => status[id]?.loginStored !== undefined || sessionOnly(id));
    progress.current = {};
    for (const id of ids) {
      progress.current[id] = status[id]?.loginStored || sessionOnly(id) ? { state: 'waiting' } : { state: 'skipped', note: 'No login stored — add one under this carrier' };
    }
    onProgress(progress.current);

    for (const id of ids) {
      if (stopRef.current) { if (progress.current[id].state === 'waiting') set(id, { state: 'skipped', note: 'Stopped' }); continue; }
      if (progress.current[id].state === 'skipped') continue;
      set(id, { state: 'working' });
      if (sessionOnly(id)) {
        try {
          const check = await shareCarrierSession(startUrl(id)!, id);
          set(id, check.ok
            ? { state: 'in', note: 'Already logged in (same session)' }
            : { state: 'out', note: 'Not logged in — sign in to it by hand in the carrier browser (its sign-in needs a person).' });
        } catch {
          set(id, { state: 'out', note: 'The automation server on this computer is not running' });
        }
        continue;
      }
      try {
        const runId = await runJob('carrier-log-in', [id]);
        currentRun.current = runId;
        set(id, { state: 'working', runId });
        let showingCard = false;
        for (;;) {
          await new Promise((r) => setTimeout(r, 1500));
          const s = await getStatus(runId);
          if (s.status === 'needs_agent_action') {
            if (!showingCard) { set(id, { state: 'needs-you', card: await getHandoffLink(runId), runId }); showingCard = true; }
            continue;
          }
          if (s.status === 'running') { if (showingCard) { set(id, { state: 'working', runId }); showingCard = false; } continue; }
          const r = s.loginJson;
          // Cancelled with the X (or Stop): skip this carrier and carry on with the next.
          if (s.attentionMessage === 'Cancelled by the person') set(id, { state: 'skipped', note: 'Cancelled — skipped' });
          else if (r?.loggedIn) set(id, { state: 'in', note: r.how === 'already-logged-in' ? 'Already logged in' : r.how === 'one-time-code' ? 'Logged in with your code' : 'Logged in' });
          else set(id, { state: 'out', note: r?.note || (s.status === 'needs_manual_login' ? s.attentionMessage ?? 'The carrier browser is not running' : 'The login did not finish') });
          break;
        }
      } catch {
        set(id, { state: 'out', note: 'The automation server on this computer is not running' });
      }
    }
    setRunning(false);
    onFinished?.();
  };

  return (
    <button
      type="button"
      className="ca-header__action ca-header__action--primary"
      onClick={running ? () => { stopRef.current = true; if (currentRun.current) void cancelRun(currentRun.current); } : start}
      title={running ? 'Stop now: cancels the login in progress and skips the rest' : 'Log the carrier browser in to every carrier with a stored login, one at a time'}
    >
      {running ? <FiX size={16} /> : <FiLock size={16} />}
      {running ? 'Stop' : 'Log in to all carriers'}
    </button>
  );
};

export default LogInAllCarriers;
